// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions
// tslink:disable:no-any

import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import {
  EventHandlerPayloadResolveMoment,
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { confirmToPersistBeforeLeaving } from '@epicuro-next/state/config-strip-state';
import * as pbasketActions from '@epicuro-next/state/p-basket-state';
import { go, openOverlayOutlet } from '@epicuro-next/state/router-store';
import { Replace, StrictOmit } from '@epicuro-next/utilities';

import * as loadingStateWrapperHelpers from '../../loading-state-wrapper-helpers';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:below-config-strip-root',
  property: 'panelName',
};

export function createRecommendationSuggestRequestBodyArg([
  panelName,
  [domainId, brandOrganisations],
  configStripSelectedVariant,
]: [
  panelName: string,
  accountDetails: [domainId: string, brandOrganisations: { brandId: string }[]],
  configStripSelectedVariant: string,
]): string {
  const brandId = brandOrganisations[0].brandId;
  const body = {
    panelType: panelName,
    properties: {
      moreLikeBrandId: brandId ?? domainId,
      moreLikeProductId: configStripSelectedVariant,
    },
  };

  const bodyJson = JSON.stringify(body);

  const fetchArgs = {
    headers: {
      accept: 'application/json, text/plain, */*',
      'accept-language': 'en-US',
      'content-type': 'application/json',
    },
    body: bodyJson,
    method: 'POST',
  };

  const fetchString = `fetch("/api/v2/recommendation/suggest", ${JSON.stringify(
    fetchArgs,
  )});`;

  return fetchString;
}

export function transformToNavigateToPBasket(setSearchQueryPayload: {
  query: string;
}): StrictOmit<ReturnType<typeof confirmToPersistBeforeLeaving>, 'type'> {
  const continuationActions: [
    ReturnType<typeof pbasketActions.removeFilters>,
    ReturnType<typeof pbasketActions.setSearchQuery>,
    Replace<
      ReturnType<typeof pbasketActions.setSortingParams>,
      'sortDirection',
      string
    >,
  ] = [
    {
      type: '[ProductBasket] Remove Filters',
    },
    {
      type: '[ProductBasket] Set search query',
      ...setSearchQueryPayload,
    },
    {
      type: '[ProductBasket] Set the sorting parameters',
      sortField: 'Stock',
      sortDirection: 'Desc',
    },
  ];

  const navigationAction: ReturnType<typeof go> = {
    type: '[Router] Go',
    commands: ['browse'],
  };

  return {
    navigationAction,
    continuationActions,
  };
}

const headerText: IDynamicItem = {
  id: 'products-panel-header',
  contentTypeId: 'dynamic-elements-button',
  if: [
    'EQ',
    [
      false,
      {
        provider: 'parent',
        property: 'doneLoadingAndHasNoData',
      },
    ],
  ],
  styles: {
    style: {
      color:
        'var(--config-strip-add-to-basket-button-selected-color, var(--color-brand-primary, #3f51b5))',
      cursor: 'pointer',
    },
    classes: ['mr-2'],
  },
  inputs: {
    text: recommendationsPanelsLocaleKeys.similarStyles,
  },
  attributes: {
    'data-e2e': 'similar-products-button',
  },
  eventHandlers: {
    onClick: [
      {
        type: 'ngrx',
        action: confirmToPersistBeforeLeaving.type,
        payload$: {
          provider: 'parent',
          property: 'setSearchQueryPayload',
          transform: transformToNavigateToPBasket.toString(),
        },
        payloadFetchMoment:
          EventHandlerPayloadResolveMoment.AtEventAndBeforeAnyEventHandlersAreExecuted,
      },
    ],
  },
};

const somethingWentWrongIndicator: IDynamicSimpleText = {
  id: 'something-went-wrong-indicator',
  contentTypeId: 'dynamic-elements-simple-text',
  styles: {
    classes: ['text-muted'],
  },
  if: loadingStateWrapperHelpers.ifHasError(),
  inputs: {
    text: recommendationsPanelsLocaleKeys.somethingWentWrongLoadingSimilarProducts,
  },
};

const loadingIndicator: IDynamicItem = {
  id: 'products-panel-loading-indicator',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    // tslint:disable-next-line:no-duplicate-string
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'isLoading',
      },
    ],
  ],
  data: {
    nrOfItems: 10,
    items: {
      provider: 'self',
      property: 'nrOfItems',
      transform: function (nrOfItems: number) {
        return new Array(nrOfItems).fill(undefined);
      }.toString(),
    },
  },
  styles: {
    classes: ['d-flex'],
  },
  inputs: {
    contentType: {
      contentTypeId: 'dynamic-collections-renderer',
      styles: {
        classes: ['ml-1', 'd-block'],
        style: {
          width: '4rem',
          height: '4.625rem',
          'background-color': '#f5f5f5',
          'border-radius': 'var(--border-radius, 0.1875rem)',
        },
      },
    },
    propertyToUse: 'irrelevant',
    items$: {
      provider: 'self',
      property: 'items',
    },
  },
};

export function transformToOpenConfigStrip(productDataArg: {
  configStrip: { overlayPath: string[] };
}): StrictOmit<ReturnType<typeof confirmToPersistBeforeLeaving>, 'type'> {
  const continuationActions: [ReturnType<typeof openOverlayOutlet>] = [
    {
      type: '[Router] Open Overlay',
      overlayPath: productDataArg.configStrip.overlayPath,
    },
  ];

  return {
    continuationActions,
  };
}

const itemsPanelItem: IDynamicItem = {
  id: 'product-panel-item',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['ml-1'],
    style: {
      width: '4rem',
      height: '4.625rem',
      'border-radius': 'var(--border-radius, 0.1875rem)',
      cursor: 'pointer',
    },
  },
  inputs: {
    media: {
      provider: 'self',
      property: 'productData',
      transform: '(productData) => productData.media',
    },
    maxHeight: '4.625rem',
  },
  attributes: {
    'data-e2e': 'similar-products-thumbnail',
    'data-productid': {
      provider: 'self',
      property: 'productData',
      transform: function (productData: SingleProductDataType): string {
        return productData.product.productId;
      }.toString(),
    },
  },
  eventHandlers: {
    onClick: [
      {
        type: 'ngrx',
        action: confirmToPersistBeforeLeaving.type,
        payload$: {
          provider: 'self',
          property: 'productData',
          transform: transformToOpenConfigStrip.toString(),
        },
      },
    ],
  },
};

const itemsPanel: IDynamicItem = {
  id: 'product-panel-items',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    // tslint:disable-next-line:no-duplicate-string
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'hasData',
      },
    ],
  ],
  styles: {
    classes: ['d-flex'],
    style: {
      cursor: 'pointer',
    },
  },
  data: {
    // The parent of the contentType used in the array renderer is this array renderer, hence the
    // productData needs to be available on the array renderer itself.
    productData: {
      provider: 'parent',
      property: 'productData',
    },
  },
  inputs: {
    contentType: itemsPanelItem,
    propertyToUse: 'productData',
    sliceEnd: 5,
    items$: {
      provider: 'parent',
      property: 'productData',
    },
  },
};

const noItemsPanel: IDynamicSimpleText = {
  id: 'product-panel-no-items',
  contentTypeId: 'dynamic-elements-simple-text',
  if: [
    // tslint:disable-next-line:no-duplicate-string
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'doneLoadingAndHasNoData',
      },
    ],
  ],
  inputs: {
    text: recommendationsPanelsLocaleKeys.noSimilarProductsFound,
  },
};

interface HttpResponseBodyType {
  result: {
    dataSet: {
      item: {
        brandId: string;
        productId: string;
        masterProductId: string;
        title: string;
        description: string;
        baseImageUrl: string;
        // tslint:disable-next-line:no-any
        attributes: { [index: string]: { value: any }[] };
      };
    }[];
    metadata: {
      provider: string;
      processors: number[];
    };
  };
  operationId: string;
  hash: string;
}

type SingleProductDataType = {
  correlationId: string;
  media: {
    url: string;
    mediaType: string;
  };
  configStrip: {
    overlayPath: string[];
  };
  product: {
    brandId: string;
    productId: string;
    masterProductId: string;
    title: string;
    description: string;
    baseImageUrl: string;
  };
};

type ProductDataType = {
  products: {
    media: {
      url: string;
      mediaType: string;
    };
    configStrip: {
      overlayPath: string[];
    };
    product: {
      brandId: string;
      productId: string;
      masterProductId: string;
      title: string;
      description: string;
      baseImageUrl: string;
    };
  }[];
  correlationId: string;
};

export function httpResponseBodyToProductData(
  body: HttpResponseBodyType,
): ProductDataType {
  const data = body;
  const products = data.result.dataSet.map((dataItem) => dataItem.item);

  const mappedProducts = products.map((product) => {
    const complexObject = {
      media: {
        url: product.baseImageUrl,
        mediaType: 'image',
      },
      configStrip: {
        overlayPath: [
          'cs',
          product.brandId,
          product.masterProductId,
          product.productId,
        ],
      },
      product,
    };

    return complexObject;
  });

  const returnValue = {
    products: mappedProducts,
    correlationId: data.operationId,
  };

  return returnValue;
}

export function productDataToProductIdsQueryPayload(
  productData: { product: { productId: string } }[],
) {
  const ids = productData.map(
    (productDataEntry) => productDataEntry.product.productId,
  );

  const payload = {
    query: (ids ?? []).join(' '),
  };

  return payload;
}

const productsPanel: IDynamicItem = {
  id: 'products-panel',
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    classes: ['d-flex', 'h-100', 'align-items-center', 'px-2', 'mr-2'],
  },
  if: [
    // tslint:disable-next-line:no-duplicate-string
    'OR',
    [
      {
        provider: 'parent',
        property: 'showWithoutProducts',
      },
      {
        provider: 'parent',
        property: 'hasData',
      },
    ],
  ],
  children: [
    headerText,
    somethingWentWrongIndicator,
    loadingIndicator,
    itemsPanel,
    noItemsPanel,
  ],
};

export const belowConfigStripContainer: IDynamicItem = {
  id: 'below-config-strip-container',
  contentTypeId: 'dynamic-collections-renderer',
  if: [
    'OR',
    [
      {
        provider: 'UtilitiesProviderService',
        property: 'isMobileView',
        transform: function (isMobileView: boolean) {
          return !isMobileView;
        }.toString(),
      },
      {
        provider: 'parent',
        property: 'showForMobileView',
      },
    ],
  ],
  data: {
    nrOfProductsToDisplay: 5,
    accountDetails: {
      provider: 'GlobalStoreProviderService',
      property: {
        once: true,
        paths: [
          'accounts.activeAccount.domainId',
          'accounts.activeAccount.brandOrganisations',
        ],
      },
    },
    configStripSelectedVariant: {
      provider: 'GlobalStoreProviderService',
      property: 'configStrip.ui.selectedVariant',
    },
    loadingStateWrapper: {
      provider: 'HttpBodyProvider',
      property: {
        values: [
          panelNameProvider,
          {
            provider: 'self',
            property: 'accountDetails',
          },
          {
            provider: 'self',
            property: 'configStripSelectedVariant',
          },
        ],
        transform: createRecommendationSuggestRequestBodyArg.toString(),
      },
      transform: httpResponseBodyToProductData.toString(),
      wrapWithLoadingState: true,
    },
    isLoading: {
      provider: 'self',
      property: 'loadingStateWrapper',
      transform: loadingStateWrapperHelpers.isLoadingTransform.toString(),
    },
    hasError: {
      provider: 'self',
      property: 'loadingStateWrapper',
      transform: loadingStateWrapperHelpers.hasErrorTransform.toString(),
    },
    productData: {
      provider: 'self',
      property: 'loadingStateWrapper',
      transform: function (loadingStateWrapper: {
        isLoading: boolean;
        data?: ReturnType<typeof httpResponseBodyToProductData>;
      }): SingleProductDataType[] {
        const data = loadingStateWrapper.data;

        if (loadingStateWrapper.isLoading || data === undefined) {
          return [];
        }

        const products = data.products;

        const result = products.map((product) => ({
          ...product,
          correlationId: data.correlationId,
        }));

        return result;
      }.toString(),
    },
    hasData: {
      provider: 'self',
      property: 'productData',
      transform: function (productData: []) {
        return productData.length > 0;
      }.toString(),
    },
    doneLoadingAndHasNoData: {
      values: [
        {
          provider: 'self',
          property: 'hasData',
        },
        {
          provider: 'self',
          property: 'isLoading',
        },
        {
          provider: 'self',
          property: 'hasError',
        },
      ],
      transform: function ([hasData, isLoading, hasError]: [
        boolean,
        boolean,
        boolean,
      ]) {
        return !hasError && !isLoading && !hasData;
      }.toString(),
    },
    setSearchQueryPayload: {
      provider: 'self',
      property: 'productData',
      transform: productDataToProductIdsQueryPayload.toString(),
      onError: {
        type: 'ngrx',
        action: '[Sentry] Log Error',
      },
    },
    showWithoutProducts: true,
  },
  children: [productsPanel],
};

export const belowConfigStrip: IDynamicItem = {
  id: 'below-config-strip-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName: 'BelowConfigStrip',
    showForMobileView: false,
  },
  children: [belowConfigStripContainer],
};
